import { NotificationsContext, type NotificationsContextValue } from '@/app/providers/notifications-context';
import { deleteAllReadNotifications, deleteNotification } from '@/features/delete-notification/api';
import { markAllNotificationsRead, markNotificationRead } from '@/features/mark-notification-read/api';
import type { UserRole } from '@/shared/config/feature';
import { useNotificationsRealtime, type CreatedPayload } from '@/widgets/notifications-realtime/useNotificationsRealtime';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

interface Props {
    role: UserRole | null;
    initialUnreadCount: number;
    children: ReactNode;
}

export function NotificationsProvider({ role, initialUnreadCount, children }: Props) {
    // Every authenticated user gets notifications — license file updates go to
    // ordinary licence holders, not just staff.
    const enabled = role !== null;
    const [unreadCount, setUnreadCount] = useState(initialUnreadCount);
    const createdListeners = useRef(new Set<(payload: CreatedPayload) => void>());

    // The single realtime subscription for the whole app. onUnreadCount owns the
    // shared count; onCreated fans out to per-consumer list listeners.
    useNotificationsRealtime({
        enabled,
        onCreated: (payload) => {
            createdListeners.current.forEach((fn) => fn(payload));
        },
        onUnreadCount: setUnreadCount,
    });

    const markRead = useCallback(async (id: string) => {
        const unread = await markNotificationRead(id);
        if (unread !== null) setUnreadCount(unread);
        return unread;
    }, []);

    const markAllRead = useCallback(async () => {
        const unread = await markAllNotificationsRead();
        if (unread !== null) setUnreadCount(unread);
        return unread;
    }, []);

    const deleteOne = useCallback(async (id: string) => {
        const result = await deleteNotification(id);
        if (result && typeof result.unread_count === 'number') setUnreadCount(result.unread_count);
        return result;
    }, []);

    const deleteAllRead = useCallback(async () => {
        const result = await deleteAllReadNotifications();
        if (result && typeof result.unread_count === 'number') setUnreadCount(result.unread_count);
        return result;
    }, []);

    const subscribeCreated = useCallback((handler: (payload: CreatedPayload) => void) => {
        createdListeners.current.add(handler);
        return () => {
            createdListeners.current.delete(handler);
        };
    }, []);

    const value = useMemo<NotificationsContextValue>(
        () => ({ unreadCount, markRead, markAllRead, deleteOne, deleteAllRead, subscribeCreated }),
        [unreadCount, markRead, markAllRead, deleteOne, deleteAllRead, subscribeCreated],
    );

    return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
